import React, { lazy, Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import Layout from './components/Layout/Layout';
import LoginForm from './components/Auth/LoginForm';
import PasswordResetRequest from './components/Auth/PasswordResetRequest';
import PasswordResetConfirm from './components/Auth/PasswordResetConfirm';
import FileUpload from './components/Upload/FileUpload';
import FileList from './components/FileList/FileList';
import TranscriptViewer from './components/Viewer/TranscriptViewer';
import AdminPanel from './components/Admin/AdminPanel';

const MobileApp = lazy(() => import('./components/Mobile/MobileApp'));

// Detect mobile devices
function isMobileDevice() {
  if (typeof window === 'undefined') return false;
  // Check if user explicitly opted for desktop via ?desktop=1
  if (sessionStorage.getItem('prefer-desktop')) return false;
  return /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
    || (navigator.maxTouchPoints > 0 && window.innerWidth <= 1024);
}

// Redirect mobile users on dynamic routes (e.g. /transcript/:id -> /mobile/transcript/:id)
function MobileRedirectOrDesktop({ children }) {
  const location = useLocation();
  if (isMobileDevice()) {
    return <Navigate to={`/mobile${location.pathname}`} replace />;
  }
  return children;
}

// Protected route wrapper
function ProtectedRoute({ children, adminOnly = false }) {
  const { user, loading } = useAuth();

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (!user) {
    return <Navigate to="/login" replace />;
  }

  if (adminOnly && !user.is_admin) {
    return <Navigate to="/" replace />;
  }

  return children;
}

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <div className="app-loading">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Routes>
      {/* Public routes */}
      <Route
        path="/login"
        element={
          isMobileDevice() ? <Navigate to="/mobile/login" replace /> :
          user ? <Navigate to="/" replace /> : <LoginForm />
        }
      />
      <Route path="/reset-password" element={<PasswordResetRequest />} />
      <Route path="/reset-password/confirm" element={<PasswordResetConfirm />} />

      {/* Protected routes - redirect mobile users */}
      <Route
        path="/"
        element={
          isMobileDevice() ? <Navigate to="/mobile/" replace /> :
          <ProtectedRoute>
            <Layout>
              <FileUpload />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/files"
        element={
          isMobileDevice() ? <Navigate to="/mobile/files" replace /> :
          <ProtectedRoute>
            <Layout>
              <FileList />
            </Layout>
          </ProtectedRoute>
        }
      />
      <Route
        path="/transcript/:id"
        element={
          <MobileRedirectOrDesktop>
            <ProtectedRoute>
              <Layout>
                <TranscriptViewer />
              </Layout>
            </ProtectedRoute>
          </MobileRedirectOrDesktop>
        }
      />
      <Route
        path="/admin"
        element={
          isMobileDevice() ? <Navigate to="/mobile/admin" replace /> :
          <ProtectedRoute adminOnly>
            <Layout>
              <AdminPanel />
            </Layout>
          </ProtectedRoute>
        }
      />

      {/* Mobile routes */}
      <Route
        path="/mobile/*"
        element={
          <Suspense fallback={<div className="app-loading"><div className="spinner"></div></div>}>
            <MobileApp />
          </Suspense>
        }
      />

      {/* Catch all */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default App;
